import { Box, Checkbox, Chip, Typography } from '@mui/material';
import { ArrowRight, Link } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { getModalityIcon, Modality } from '~/domain/dicom/modality';
import { Image3D } from '~/domain/patient/image3d';
import { Patient } from '~/domain/patient/patient';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { ActiveItem } from '../../useActiveItems';
import { SelectedDatasets } from '../LoadedPatientDataTree';

interface RegistrationLeafProps {
    image: Image3D;
    registration: RigidRegistration;
    selectedDatasets: SelectedDatasets;
    activeItems: ActiveItem[];
    onSelect: React.Dispatch<React.SetStateAction<SelectedDatasets>>;
    patient: Patient;
}

const RegistrationLeaf = ({
    image,
    registration,
    selectedDatasets,
    activeItems,
    onSelect,
    patient,
}: RegistrationLeafProps) => {
    const targetImage = patient.images.find(
        (img) =>
            img.frameOfReferenceUid === registration.targetFrameOfReferenceUid
    );

    if (!targetImage) {
        return null;
    }

    const isRegistrationSelected =
        selectedDatasets?.registration?.dicomUid === registration.dicomUid;

    const isRegistrationActive = activeItems.some(
        (item) => item.dicomUid === registration.dicomUid
    );
    const handleSelect = () => {
        if (isRegistrationSelected) {
            onSelect((prev) => ({
                ...prev,
                registration: undefined,
                blendImage: undefined,
            }));
        } else {
            onSelect((prev) => ({
                ...prev,
                registration,
                image: image,
                blendImage: targetImage,
            }));
        }
    };

    return (
        <Box
            sx={{
                ml: 2,
                py: 0.5,
                px: 1,
                cursor: 'pointer',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                },
                ...(isRegistrationSelected && {
                    backgroundColor: 'rgba(0, 165, 185, 0.05)',
                }),
                ...(isRegistrationActive && {
                    backgroundColor: 'rgba(0, 165, 185, 0.1)',
                }),
            }}
            onClick={handleSelect}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <Checkbox
                        disableRipple
                        size="small"
                        color="primary"
                        checked={isRegistrationSelected}
                        sx={{
                            '&': {
                                padding: 0,
                            },
                        }}
                    />

                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Link size={12} />
                        <Typography>{Modality.REG}</Typography>
                    </Box>

                    <ArrowRight size={12} color="#666" />

                    <Box display="flex" alignItems="center" gap={0.5}>
                        {getModalityIcon(
                            targetImage.seriesMetadata.modality,
                            12
                        )}
                        <Typography
                            variant="body2"
                            sx={{
                                maxWidth: 150,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {targetImage.seriesMetadata.seriesDescription}
                        </Typography>
                    </Box>

                    {registration.contentLabel && (
                        <Typography variant="caption" color="text.secondary">
                            ({registration.contentLabel})
                        </Typography>
                    )}
                </Box>

                {isRegistrationActive && (
                    <Chip
                        variant="outlined"
                        size="small"
                        sx={{
                            fontSize: 10,
                            height: 20,
                            padding: 0.5,
                            lineHeight: 0.9,
                        }}
                        color="success"
                        label={<FormattedMessage id="active" />}
                    />
                )}
            </Box>
        </Box>
    );
};

export default RegistrationLeaf;
